import React, { useState } from 'react'

import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import FormSection from 'components_new/molecules/FormSection'
import FormToggle from 'components_new/molecules/FormToggle'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { useAnalysisTypeActions } from 'redux/actions/AnalysisTypes'
import { TAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useTranslation } from 'language/translator'
import { isMissingConfiguration } from 'utils/analysis'

import InvestigationDashboard from './InvestigationDashboard'

interface AnalysisTypeSettingsProps {
  analysisType: TAnalysisType
  sx?: SxProps
}

const AnalysisTypeSettings = (props: AnalysisTypeSettingsProps) => {
  const { analysisType, sx } = props

  const { t } = useTranslation()
  const { tryUpdateAnalysisTypeSettings } = useAnalysisTypeActions()

  const [dialog, setDialog] = useState(false)
  const [loading, setLoading] = useState(false)

  const { active } = analysisType

  // Turning one off is always allowed; turning one on needs its data.
  const locked = !active && isMissingConfiguration(analysisType)

  const handleConfirm = () => {
    setLoading(true)

    tryUpdateAnalysisTypeSettings(analysisType.id, { active: !active }, () => {
      setLoading(false)
      setDialog(false)
    })
  }

  return (
    <>
      <Box sx={sx}>
        <FormSection
          helperText={t('analysis_types', 'availability_description')}
          importance="optional"
          title={t('analysis_types', 'availability_title')}
        >
          <Tooltip
            title={locked ? t('global', 'missing_configuration') : undefined}
          >
            <span>
              <FormToggle
                body={t('analysis_types', 'activated_analysis_type_body')}
                disabled={locked}
                icon={{ name: 'CheckCircleOutline' }}
                onChange={() => setDialog(true)}
                title={t('analysis_types', 'activate_analysis_type')}
                value={active}
              />
            </span>
          </Tooltip>
        </FormSection>
        <InvestigationDashboard analysisType={analysisType} />
      </Box>

      <SimpleDialog
        actions={
          <>
            <Button onClick={() => setDialog(false)} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button loading={loading} onClick={handleConfirm}>
              {t('global', active ? 'deactivate' : 'activate')}
            </Button>
          </>
        }
        onClose={() => setDialog(false)}
        open={dialog}
        title={t(
          'analysis_types',
          active ? 'deactivate_analysis_type' : 'activate_analysis_type'
        )}
      >
        <Text>
          {t(
            'analysis_types',
            active
              ? 'deactivate_analysis_type_confirm'
              : 'activate_analysis_type_confirm'
          )}
        </Text>
      </SimpleDialog>
    </>
  )
}

export default AnalysisTypeSettings
